import { useMemo } from 'react';
import { useAllUsers } from '@/hooks/useAllUsers';
import type { PaySchedule, UserId } from '@/types';
import { DEFAULT_PAY_SCHEDULE } from '@/utils/date';
import { UNKNOWN_USER_LABEL } from '@/utils/monthlyBreakdown';

export { UNKNOWN_USER_LABEL };

/**
 * Builds a `uid -> displayName` lookup from every user profile, for pages
 * that need to render a name instead of a raw Firebase Auth uid (daily
 * dispatch, monthly breakdown, etc). Falls back to `UNKNOWN_USER_LABEL`
 * rather than the raw uid so an unresolved id is never shown to admins.
 */
export const useUserNameLookup = () => {
  const { users, loading } = useAllUsers();

  const nameByUserId = useMemo(() => {
    const map: Record<UserId, string> = {};
    for (const user of users) map[user.uid] = user.displayName;
    return map;
  }, [users]);

  const payScheduleByUserId = useMemo(() => {
    const map: Record<UserId, PaySchedule> = {};
    for (const user of users) {
      map[user.uid] = user.paySchedule ?? DEFAULT_PAY_SCHEDULE;
    }
    return map;
  }, [users]);

  const resolveName = (userId: UserId | null | undefined) =>
    (userId && nameByUserId[userId]) || UNKNOWN_USER_LABEL;

  return { nameByUserId, payScheduleByUserId, resolveName, loading };
};
